import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Users, Receipt, Plus, Trash2, Check, Undo2, Pencil } from 'lucide-react';
import { toast } from 'sonner';

const currentMonth = () => new Date().toISOString().slice(0, 7);
const payrollWindowStart = (() => {
  const d = new Date();
  d.setMonth(d.getMonth() - 2);
  return d.toISOString().slice(0, 7);
})();
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const SummaryPill = ({ label, value, color }) => (
  <div className="flex-1 min-w-[140px] bg-white rounded-xl shadow-sm p-4">
    <div className="text-xs font-medium text-slate-500">{label}</div>
    <div className={`text-2xl font-bold ${color}`}>{value}</div>
  </div>
);

const EmployeeDialog = ({ employee, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(
    employee || { name: '', designation: '', monthly_salary: '', status: 'active', joined_on: '' }
  );
  const [saving, setSaving] = useState(false);
  const editing = Boolean(employee);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.designation.trim() || form.monthly_salary === '') {
      return toast.error('Name, designation and monthly salary are required');
    }
    setSaving(true);
    try {
      const payload = { ...form, monthly_salary: Number(form.monthly_salary) };
      if (editing) await axios.patch(`${API}/finance/employees/${employee.id}`, payload);
      else await axios.post(`${API}/finance/employees`, payload);
      toast.success(editing ? 'Employee updated' : 'Employee added');
      setOpen(false);
      if (!editing) setForm({ name: '', designation: '', monthly_salary: '', status: 'active', joined_on: '' });
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save employee');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {editing ? (
          <Button variant="ghost" size="sm" data-testid={`edit-employee-${employee.id}`}>
            <Pencil className="w-4 h-4" />
          </Button>
        ) : (
          <Button className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full" data-testid="add-employee-button">
            <Plus className="w-4 h-4 mr-1" /> Add Employee
          </Button>
        )}
      </DialogTrigger>
      <DialogContent data-testid="employee-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit employee' : 'Add employee'}</DialogTitle>
          <DialogDescription>Payroll rows are generated each month from the monthly salary.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="emp-name">Employee name</Label>
            <Input id="emp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="employee-name-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="emp-desig">Designation</Label>
            <Input id="emp-desig" value={form.designation}
              onChange={(e) => setForm({ ...form, designation: e.target.value })} data-testid="employee-designation-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="emp-salary">Monthly salary (₹)</Label>
              <Input id="emp-salary" type="number" min="0" value={form.monthly_salary}
                onChange={(e) => setForm({ ...form, monthly_salary: e.target.value })} data-testid="employee-salary-input" />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="employee-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive / Left</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="emp-joined">Joined on (optional)</Label>
            <Input id="emp-joined" type="date" value={form.joined_on || ''}
              onChange={(e) => setForm({ ...form, joined_on: e.target.value })} data-testid="employee-joined-input" />
          </div>
          <Button type="submit" disabled={saving}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-full h-11"
            data-testid="employee-save-button">
            {saving ? 'Saving...' : editing ? 'Save changes' : 'Add employee'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ExpenseDialog = ({ categories, expense, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const editing = Boolean(expense);
  const [form, setForm] = useState(
    expense || {
      title: '', category: 'Rent', amount: '', expense_date: new Date().toISOString().slice(0, 10),
      status: 'paid', notes: '',
    }
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || form.amount === '') return toast.error('Title and amount are required');
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        category: form.category,
        amount: Number(form.amount),
        expense_date: form.expense_date,
        status: form.status,
        notes: form.notes || '',
      };
      if (editing) await axios.patch(`${API}/finance/expenses/${expense.id}`, payload);
      else await axios.post(`${API}/finance/expenses`, payload);
      toast.success(editing ? 'Expense updated' : 'Expense recorded');
      setOpen(false);
      if (!editing) setForm({ ...form, title: '', amount: '', notes: '' });
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save expense');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {editing ? (
          <Button variant="ghost" size="sm" title="Edit expense" data-testid={`edit-expense-${expense.id}`}>
            <Pencil className="w-4 h-4" />
          </Button>
        ) : (
          <Button className="bg-violet-600 hover:bg-violet-700 text-white rounded-full" data-testid="add-expense-button">
            <Plus className="w-4 h-4 mr-1" /> Add Expense
          </Button>
        )}
      </DialogTrigger>
      <DialogContent data-testid="expense-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit expense' : 'Add company expense'}</DialogTitle>
          <DialogDescription>Track a company cost with its category, date and payment status.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="exp-title">Title</Label>
            <Input id="exp-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Office rent - June" data-testid="expense-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="exp-amount">Amount (₹)</Label>
              <Input id="exp-amount" type="number" min="0" value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="exp-date">Date</Label>
              <Input id="exp-date" type="date" value={form.expense_date}
                onChange={(e) => setForm({ ...form, expense_date: e.target.value })} data-testid="expense-date-input" />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="expense-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="unpaid">Unpaid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="exp-notes">Notes (optional)</Label>
            <Textarea id="exp-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="expense-notes-input" />
          </div>
          <Button type="submit" disabled={saving}
            className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full h-11"
            data-testid="expense-save-button">
            {saving ? 'Saving...' : editing ? 'Save changes' : 'Add expense'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ExpensesManagement = () => {
  const [month, setMonth] = useState(currentMonth());
  const [salaries, setSalaries] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [expenses, setExpenses] = useState(null);
  const [expenseMonth, setExpenseMonth] = useState(currentMonth());

  const loadSalaries = useCallback(async () => {
    try {
      const [s, e] = await Promise.all([
        axios.get(`${API}/finance/salaries`, { params: { month } }),
        axios.get(`${API}/finance/employees`),
      ]);
      setSalaries(s.data);
      setEmployees(e.data);
    } catch (error) {
      console.warn('Salary load failed:', error?.message);
      toast.error(error.response?.data?.detail || 'Failed to load salaries');
    }
  }, [month]);

  const loadExpenses = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/finance/expenses`, { params: { month: expenseMonth } });
      setExpenses(data);
    } catch (error) {
      console.warn('Expense load failed:', error?.message);
      toast.error(error.response?.data?.detail || 'Failed to load expenses');
    }
  }, [expenseMonth]);

  useEffect(() => { loadSalaries(); }, [loadSalaries]);
  useEffect(() => { loadExpenses(); }, [loadExpenses]);

  const toggleSalary = async (row) => {
    try {
      await axios.patch(`${API}/finance/salaries/${row.id}`, {
        status: row.status === 'paid' ? 'unpaid' : 'paid',
      });
      toast.success(row.status === 'paid' ? 'Marked unpaid' : `Salary paid to ${row.employee_name}`);
      loadSalaries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update salary');
    }
  };

  const removeEmployee = async (row) => {
    if (!window.confirm(`Remove ${row.name} from payroll? Unpaid salary rows will be deleted.`)) return;
    try {
      await axios.delete(`${API}/finance/employees/${row.id}`);
      toast.success('Employee removed');
      loadSalaries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove employee');
    }
  };

  const toggleExpense = async (row) => {
    try {
      await axios.patch(`${API}/finance/expenses/${row.id}`, { status: row.status === 'paid' ? 'unpaid' : 'paid' });
      loadExpenses();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update expense');
    }
  };

  const removeExpense = async (row) => {
    if (!window.confirm(`Delete expense "${row.title}"?`)) return;
    try {
      await axios.delete(`${API}/finance/expenses/${row.id}`);
      toast.success('Expense removed');
      loadExpenses();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove expense');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="expenses-page">
      <div className="mb-6">
        <h1 className="text-4xl font-bold text-slate-900 mb-2">Expenses & Salaries</h1>
        <p className="text-lg text-slate-600">Employee payroll and company spending</p>
      </div>

      <Tabs defaultValue="salaries">
        <TabsList className="mb-6">
          <TabsTrigger value="salaries" data-testid="tab-salaries">
            <Users className="w-4 h-4 mr-2" /> Employee Salaries
          </TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses">
            <Receipt className="w-4 h-4 mr-2" /> Company Expenses
          </TabsTrigger>
        </TabsList>

        <TabsContent value="salaries">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
            <div className="space-y-1">
              <Label htmlFor="salary-month" className="text-xs text-slate-500">Payroll month</Label>
              <Input id="salary-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                className="w-44 bg-white" data-testid="salary-month-input" />
            </div>
            <EmployeeDialog onSaved={loadSalaries} />
          </div>

          <div className="flex flex-wrap gap-3 mb-6">
            <SummaryPill label="Payroll total" value={inr(salaries?.summary.total)} color="text-indigo-600" />
            <SummaryPill label="Paid" value={inr(salaries?.summary.paid)} color="text-emerald-600" />
            <SummaryPill label="Unpaid" value={inr(salaries?.summary.unpaid)} color="text-amber-600" />
            <SummaryPill label="Employees" value={salaries?.summary.employee_count ?? 0} color="text-slate-700" />
          </div>

          <Card className="border-0 shadow-lg">
            <CardHeader><CardTitle>Salaries · {salaries?.month}</CardTitle></CardHeader>
            <CardContent>
              {!salaries?.rows?.length ? (
                <p className="text-center text-slate-500 py-8" data-testid="no-employees">
                  {month < payrollWindowStart || month > currentMonth()
                    ? 'Payroll is only tracked for the current month and the previous two months.'
                    : 'No employees yet. Add your first employee to start tracking payroll.'}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="salaries-table">
                    <thead>
                      <tr className="text-left text-slate-500 border-b">
                        <th className="py-2 pr-4">Employee</th>
                        <th className="py-2 px-2">Designation</th>
                        <th className="py-2 px-2">Monthly salary</th>
                        <th className="py-2 px-2">Status</th>
                        <th className="py-2 px-2 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salaries.rows.map((r) => {
                        const emp = employees.find((e) => e.id === r.employee_id);
                        return (
                          <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50"
                            data-testid={`salary-row-${r.id}`}>
                            <td className="py-2 pr-4 font-medium text-slate-800">{r.employee_name}</td>
                            <td className="py-2 px-2 text-slate-600">{r.designation}</td>
                            <td className="py-2 px-2 font-semibold">{inr(r.amount)}</td>
                            <td className="py-2 px-2">
                              <Badge className={r.status === 'paid'
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-amber-100 text-amber-700'} data-testid={`salary-status-${r.id}`}>
                                {r.status}
                              </Badge>
                            </td>
                            <td className="py-2 px-2 text-right whitespace-nowrap">
                              <Button size="sm" variant={r.status === 'paid' ? 'outline' : 'default'}
                                className={r.status === 'paid' ? 'rounded-full' : 'rounded-full bg-emerald-600 hover:bg-emerald-700 text-white'}
                                onClick={() => toggleSalary(r)} data-testid={`toggle-salary-${r.id}`}>
                                {r.status === 'paid'
                                  ? (<><Undo2 className="w-3.5 h-3.5 mr-1" /> Mark unpaid</>)
                                  : (<><Check className="w-3.5 h-3.5 mr-1" /> Mark paid</>)}
                              </Button>
                              {emp && <EmployeeDialog employee={emp} onSaved={loadSalaries} />}
                              {emp && (
                                <Button variant="ghost" size="sm" onClick={() => removeEmployee(emp)}
                                  className="text-rose-600" title="Remove employee" data-testid={`delete-employee-${emp.id}`}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="expenses">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
            <div className="space-y-1">
              <Label htmlFor="expense-month" className="text-xs text-slate-500">Month</Label>
              <Input id="expense-month" type="month" value={expenseMonth}
                onChange={(e) => setExpenseMonth(e.target.value)} className="w-44 bg-white"
                data-testid="expense-month-input" />
            </div>
            <ExpenseDialog categories={expenses?.categories || ['Misc']} onSaved={loadExpenses} />
          </div>

          <div className="flex flex-wrap gap-3 mb-6">
            <SummaryPill label="Total expenses" value={inr(expenses?.summary.total)} color="text-violet-600" />
            <SummaryPill label="Paid" value={inr(expenses?.summary.paid)} color="text-emerald-600" />
            <SummaryPill label="Unpaid" value={inr(expenses?.summary.unpaid)} color="text-amber-600" />
            <SummaryPill label="Entries" value={expenses?.summary.count ?? 0} color="text-slate-700" />
          </div>

          <Card className="border-0 shadow-lg">
            <CardHeader><CardTitle>Company expenses</CardTitle></CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2 mb-4">
                {Object.entries(expenses?.by_category || {}).map(([c, v]) => (
                  <Badge key={c} variant="secondary" className="bg-violet-50 text-violet-700">{c}: {inr(v)}</Badge>
                ))}
              </div>
              {!expenses?.rows?.length ? (
                <p className="text-center text-slate-500 py-8" data-testid="no-expenses">
                  No expenses recorded for this month.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="expenses-table">
                    <thead>
                      <tr className="text-left text-slate-500 border-b">
                        <th className="py-2 pr-4">Title</th>
                        <th className="py-2 px-2">Category</th>
                        <th className="py-2 px-2">Date</th>
                        <th className="py-2 px-2">Amount</th>
                        <th className="py-2 px-2">Status</th>
                        <th className="py-2 px-2 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expenses.rows.map((r) => (
                        <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50"
                          data-testid={`expense-row-${r.id}`}>
                          <td className="py-2 pr-4">
                            <div className="font-medium text-slate-800">{r.title}</div>
                            {r.notes && <div className="text-xs text-slate-500">{r.notes}</div>}
                          </td>
                          <td className="py-2 px-2 text-slate-600">{r.category}</td>
                          <td className="py-2 px-2 text-slate-600">{r.expense_date}</td>
                          <td className="py-2 px-2 font-semibold">{inr(r.amount)}</td>
                          <td className="py-2 px-2">
                            <Badge className={r.status === 'paid'
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-amber-100 text-amber-700'} data-testid={`expense-status-${r.id}`}>
                              {r.status}
                            </Badge>
                          </td>
                          <td className="py-2 px-2 text-right whitespace-nowrap">
                            <Button size="sm" variant="outline" className="rounded-full"
                              onClick={() => toggleExpense(r)} data-testid={`toggle-expense-${r.id}`}>
                              {r.status === 'paid' ? 'Mark unpaid' : 'Mark paid'}
                            </Button>
                            <ExpenseDialog categories={expenses.categories} expense={r} onSaved={loadExpenses} />
                            <Button variant="ghost" size="sm" className="text-rose-600" title="Delete expense"
                              onClick={() => removeExpense(r)} data-testid={`delete-expense-${r.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ExpensesManagement;
